import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useAuth } from "../contexts/AuthContext";
import { Hand, CheckCircle, Clock, Warning, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Acknowledge → Action SLA panel.
 * Renders on Job and Job Request detail pages.
 *
 * Props:
 *   entityType: "jobs" | "job-requests"
 *   entity: the loaded entity object (must have id, acknowledged_*, actioned_*, sla_state)
 *   onChange: callback fired after a successful action / clear
 */
const STATE_STYLES = {
  pending:     { label: "Pending Action",      ring: "border-slate-200 dark:border-slate-700",       bg: "bg-slate-50 dark:bg-slate-800/40",       text: "text-slate-700 dark:text-slate-200",  Icon: Clock },
  info_pinged: { label: "Pinged — Awaiting",   ring: "border-blue-200 dark:border-blue-800/50",      bg: "bg-blue-50 dark:bg-blue-900/20",          text: "text-blue-700 dark:text-blue-300",    Icon: Clock },
  amber:       { label: "AMBER — Overdue",     ring: "border-amber-300 dark:border-amber-700/60",    bg: "bg-amber-50 dark:bg-amber-900/20",        text: "text-amber-800 dark:text-amber-300", Icon: Warning },
  critical:    { label: "CRITICAL — Escalated",ring: "border-red-300 dark:border-red-800/60",        bg: "bg-red-50 dark:bg-red-900/20",            text: "text-red-700 dark:text-red-300",     Icon: Warning },
  actioned:    { label: "Actioned",            ring: "border-emerald-300 dark:border-emerald-800/60",bg: "bg-emerald-50 dark:bg-emerald-900/20",    text: "text-emerald-700 dark:text-emerald-300", Icon: CheckCircle },
};

function formatDuration(fromIso, toIso) {
  if (!fromIso || !toIso) return "";
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return hh ? `${d}d ${hh}h` : `${d}d`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export const BREACH_REASONS = [
  { value: "client_unresponsive", label: "Client unresponsive" },
  { value: "supplier_holiday",    label: "Supplier holiday / unavailable" },
  { value: "complex_quote",       label: "Complex / waiting on info" },
  { value: "out_of_hours",        label: "Out of hours / weekend" },
  { value: "other",               label: "Other" },
];

export default function SLAActionPanel({ entityType, entity, onChange }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [showBreach, setShowBreach] = useState(false);
  const [breachReason, setBreachReason] = useState("");
  const [breachOther, setBreachOther] = useState("");
  const state = entity?.sla_state || (entity?.actioned_at ? "actioned" : "pending");
  const cfg = STATE_STYLES[state] || STATE_STYLES.pending;
  const Icon = cfg.Icon;

  // Re-render every minute so "elapsed" labels stay live for unactioned items
  useEffect(() => {
    if (entity?.actioned_at) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, [entity?.actioned_at]);

  if (!entity) return null;
  const canAction = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const isActioned = !!entity.actioned_at;

  const elapsed = isActioned
    ? formatDuration(entity.acknowledged_at, entity.actioned_at)
    : formatDuration(entity.acknowledged_at, new Date().toISOString());
  // tick is referenced so the live elapsed label re-renders on the interval
  void tick;

  const wasBreached = (entity?.sla_pings || []).some(p => ["amber", "critical"].includes(p?.stage));

  const claim = async (reason = "", other = "") => {
    if (!canAction) return;
    setBusy(true);
    try {
      const payload = {};
      if (reason) payload.breach_reason = reason;
      if (other) payload.breach_reason_other = other;
      const { data } = await API.post(`/sla/${entityType}/${entity.id}/action`, payload);
      toast.success(data.duration ? `Actioned • ${data.duration}` : "Actioned");
      setShowBreach(false);
      setBreachReason("");
      setBreachOther("");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to action");
    } finally { setBusy(false); }
  };

  const handleClaimClick = () => {
    if (wasBreached) {
      setShowBreach(true);
    } else {
      claim();
    }
  };

  const clear = async () => {
    if (!isAdmin) return;
    if (!window.confirm("Reset the actioned state? The SLA clock will start over from now.")) return;
    setBusy(true);
    try {
      await API.post(`/sla/${entityType}/${entity.id}/clear-action`);
      toast.success("Action cleared — SLA restarted");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset");
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`mb-4 rounded-md border ${cfg.ring} ${cfg.bg} px-4 py-3 flex items-center gap-3 flex-wrap`}
      data-testid="sla-action-panel"
    >
      <Icon size={20} weight={state === "actioned" ? "fill" : "regular"} className={cfg.text} />
      <div className="flex-1 min-w-[180px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-widest font-semibold ${cfg.text}`} data-testid="sla-state-label">{cfg.label}</span>
          {entity.acknowledged_by_name && (
            <span className="text-[11px] text-muted-foreground" data-testid="sla-acknowledged-by">
              Acknowledged by <strong>{entity.acknowledged_by_name}</strong>
              {entity.acknowledged_at ? ` • ${formatDate(entity.acknowledged_at)}` : ""}
            </span>
          )}
        </div>
        {isActioned ? (
          <p className="text-xs mt-0.5 font-medium text-foreground" data-testid="sla-actioned-by">
            <CheckCircle size={12} weight="fill" className="inline mr-1 text-emerald-600" />
            Actioned by <strong>{entity.actioned_by_name}</strong> • {elapsed} after acknowledgment
          </p>
        ) : (
          <p className="text-xs mt-0.5 text-muted-foreground" data-testid="sla-elapsed-label">
            {elapsed ? `${elapsed} elapsed since acknowledgment — no one has actioned this yet.` : "Awaiting first action."}
          </p>
        )}
      </div>
      {!isActioned && canAction && (
        <Button onClick={handleClaimClick} disabled={busy} size="sm" data-testid="sla-action-claim-btn">
          <Hand size={14} className="mr-1" /> I'll Action This
        </Button>
      )}
      {isActioned && isAdmin && (
        <Button onClick={clear} disabled={busy} size="sm" variant="outline" data-testid="sla-action-clear-btn">
          <ArrowCounterClockwise size={14} className="mr-1" /> Reset
        </Button>
      )}

      {/* Phase 258 — breach reason dialog (only when item already breached) */}
      <Dialog open={showBreach} onOpenChange={(o) => { if (!busy) setShowBreach(o); }}>
        <DialogContent data-testid="sla-breach-dialog">
          <DialogHeader>
            <DialogTitle>This breached SLA — why?</DialogTitle>
            <DialogDescription>
              Optional. Logging the reason helps spot patterns and improve our process.
              You can skip this if it's a one-off.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={breachReason} onValueChange={setBreachReason}>
                <SelectTrigger data-testid="sla-breach-reason-select">
                  <SelectValue placeholder="Pick a reason (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {BREACH_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {breachReason === "other" && (
              <div>
                <Label className="text-xs">Tell us more</Label>
                <Textarea
                  value={breachOther}
                  onChange={(e) => setBreachOther(e.target.value)}
                  placeholder="One-line context…"
                  rows={2}
                  data-testid="sla-breach-other-input"
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => claim()} disabled={busy} data-testid="sla-breach-skip-btn">
              Skip & action
            </Button>
            <Button onClick={() => claim(breachReason, breachOther)} disabled={busy || !breachReason} data-testid="sla-breach-confirm-btn">
              Action with reason
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
